/**
 * Global axios variant routing interceptor — sandbox only.
 *
 * The `api` instance (fetcher.ts) handles requests made via fetchJson / axiosApi
 * by overriding baseURL.  That approach works for relative-URL requests but does
 * NOT affect the ~100+ API modules that call global axios with absolute URLs:
 *
 *   axios.get(`${config.API_BASE_URL}/api/v3/…`, { … })
 *
 * Those calls bypass the api-instance interceptor and always target the main
 * sandbox LOS, so a user who has selected a variant sees their LOS-side changes
 * reflected in the dealer portal only for the subset of calls that happen to
 * go through fetchJson — every other call silently falls back to main LOS.
 *
 * This module registers a second interceptor directly on the global axios
 * instance that rewrites any absolute URL whose origin matches
 * config.API_BASE_URL to the active variant origin.  It is registered once at
 * app startup (index.tsx) and is a complete no-op in every non-sandbox
 * environment — a persisted cookie cannot reroute traffic outside sandbox even
 * if it somehow survives an environment change.
 */

import axios, { InternalAxiosRequestConfig } from 'axios'
import appConfig from 'config'
import { getLosVariantBaseUrl } from 'hooks/useLosVersion'

/**
 * Pure interceptor function — exported for unit testing.
 *
 * Rewrites `req.url` when:
 *   1. The runtime environment is 'sandbox'.
 *   2. A valid variant cookie is present (getLosVariantBaseUrl returns non-null).
 *   3. The request URL is an absolute URL whose origin matches API_BASE_URL.
 *
 * All other requests pass through unchanged.
 */
export function sandboxVariantRoutingInterceptor(
    req: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
    // Belt-and-suspenders: never reroute outside sandbox regardless of cookies.
    if (appConfig.ENVIRONMENT !== 'sandbox') return req

    const variantBase = getLosVariantBaseUrl()

    if (!variantBase) return req

    if (req.url?.startsWith(appConfig.API_BASE_URL)) {
        // Replace the stable-LOS origin with the variant origin.
        // e.g. https://los.sandbox-eks.octane.co/api/v3/foo →
        //      https://los-pr-123.sandbox-eks.octane.co/api/v3/foo
        req.url = req.url.replace(appConfig.API_BASE_URL, variantBase)
    }

    return req
}

/**
 * Register the interceptor on the global axios instance.
 * Call once at app startup (index.tsx).
 */
export function registerSandboxVariantRoutingInterceptor(): void {
    axios.interceptors.request.use(sandboxVariantRoutingInterceptor, (error) =>
        Promise.reject(error),
    )
}
